import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import type { SocialLink } from '@/types'

import Link from 'next/link'
import {
    ArrowUpLeft,
    Instagram,
    Linkedin,
    Mail,
    Send,
    Youtube
} from 'lucide-react'
import Logo from '@/components/generals/logo'
import { footerColumns, siteConfig, socialLinks } from '@/constants/site'

const SOCIAL_ICONS: Record<SocialLink['network'], LucideIcon> = {
    telegram: Send,
    instagram: Instagram,
    linkedin: Linkedin,
    youtube: Youtube
}

const persianYear = (): string => {
    return new Intl.DateTimeFormat('fa-IR', { year: 'numeric' }).format(new Date())
}

const Footer = (): ReactNode => {
    return (
        <footer className='relative mt-24 border-t border-white/70 bg-white/60 backdrop-blur-sm'>
            <div className='mx-auto max-w-6xl px-5 pt-14 pb-8 sm:px-6 lg:px-8'>
                <div className='grid gap-10 md:grid-cols-2 lg:grid-cols-[1.5fr_repeat(3,1fr)]'>
                    <div className='flex flex-col gap-5'>
                        <Logo />
                        <p className='max-w-sm text-sm text-muted-foreground'>
                            دوره‌ها را در وب کشف کنید، در اپلیکیشن دسکتاپ با اسلایدهای تعاملی و مربی هوش مصنوعی بیاموزید و مسیر رشدتان را در داشبورد ببینید.
                        </p>

                        <ul className='flex items-center gap-2' aria-label="شبکه‌های اجتماعی">
                            {socialLinks.map((social) => {
                                const Icon = SOCIAL_ICONS[social.network]

                                return (
                                    <li key={social.network}>
                                        <a
                                            href={social.href}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            aria-label={`${siteConfig.name} در ${social.label}`}
                                            className='grid size-10 place-items-center rounded-xl bg-white text-muted-foreground shadow-soft ring-1 ring-border transition hover:-translate-y-0.5 hover:text-primary hover:ring-primary/30'
                                        >
                                            <Icon className='size-4' aria-hidden='true' />
                                        </a>
                                    </li>
                                )
                            })}
                        </ul>
                    </div>

                    {footerColumns.map((column) => (
                        <div key={column.title}>
                            <h3 className='text-sm font-bold text-foreground'>
                                {column.title}
                            </h3>
                            <ul className='mt-4 flex flex-col gap-2.5'>
                                {column.links.map((link) => (
                                    <li key={link.href + link.label}>
                                        <Link
                                            href={link.href}
                                            className='group inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-primary'
                                        >
                                            {link.label}
                                            <ArrowUpLeft className='size-3.5 opacity-0 transition-all duration-200 group-hover:-translate-x-0.5 group-hover:opacity-100' aria-hidden='true' />
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                <div className='mt-12 flex flex-col gap-4 rounded-2xl bg-white p-4 shadow-soft ring-1 ring-border sm:flex-row sm:items-center sm:justify-between sm:p-5'>
                    <div className='flex items-center gap-3'>
                        <span className='grid size-10 shrink-0 place-items-center rounded-xl bg-primary-soft text-primary'>
                            <Mail className='size-5' aria-hidden='true' />
                        </span>
                        <div>
                            <p className='text-sm font-bold text-foreground'>
                                سؤالی دارید یا به همکاری فکر می‌کنید؟
                            </p>
                            <p className='text-xs text-muted-foreground'>
                                پیامتان را از فرم تماس بفرستید تا تیم پشتیبانی پاسخ را به ایمیل شما بفرستد.
                            </p>
                        </div>
                    </div>
                    <Link
                        href='/contact'
                        className='inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-white transition hover:bg-primary-hover'
                    >
                        تماس با پشتیبانی
                    </Link>
                </div>

                <div className='mt-8 flex flex-col gap-2 border-t border-border pt-6 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        © <span suppressHydrationWarning>{persianYear()}</span> {siteConfig.name}. همه حقوق محفوظ است.
                    </p>
                    <p>
                        ساخته‌شده برای یادگیری عمیق، تعاملی و انسانی.
                    </p>
                </div>
            </div>
        </footer>
    )
}

export default Footer
